import React from 'react';
import MainLayout from '../components/layout/MainLayout';
import Section from '../components/ui/Section';
import Button from '../components/ui/Button';
import SEO from '../components/common/SEO';
import CredibilitySection from '../components/about/CredibilitySection';
import PhilosophySection from '../components/about/PhilosophySection';
import WorkProcess from '../components/about/WorkProcess';
import { motion } from 'framer-motion';

const About = () => {
  const claritySessionLink = "https://tidycal.com/1x8odpm/ai-clarity-session-1y6rlrd-mpnrkpl";

  return (
    <MainLayout>
      <SEO 
        title="About Tomar Beh | AI Leadership Advisor" 
        description="I help leaders lead AI with confidence without losing what makes their organisation human. Strategic AI guidance for social impact organisations." 
        canonical="/about" 
      />
      
      {/* Hero Section */}
      <Section bg="cream" className="pt-24 pb-20">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row gap-16 items-center">
            <div className="w-full md:w-5/12">
              <motion.div 
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="relative group"
              >
                <div className="absolute -inset-4 bg-brand-teal/10 rounded-[3rem] -z-10 transition-colors duration-500"></div>
                <img 
                  src="https://media-manager-c.questera.ai/greta-media/7c98abf852f963e06195176deed57bb9c81b4f53d205af5ca5b2f9af1b9cfaa840d35f6737e086b8644a3796bedcf0cc/images/aW1hZ2UvcG5n/9386e3a8f90e2df7f265a8726f5f25aa.png" 
                  alt="Tomar Beh, Founder of AI CoLab Studio" 
                  className="w-full h-auto object-contain drop-shadow-2xl rounded-[2.5rem]"
                />
              </motion.div>
            </div>
            <div className="w-full md:w-7/12">
              <span className="text-brand-teal font-bold text-sm uppercase tracking-widest mb-4 block">Trusted Leadership Advisor</span>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-brand-navy mb-8 leading-[1.1]">
                Hi, I'm Tomar.<br />
                <span className="text-brand-teal">I help leaders lead AI with confidence without losing what makes them human.</span>
              </h1>
              <div className="space-y-6 text-gray-700 text-xl leading-relaxed font-medium">
                <p>
                  AI is changing every organisation. For social impact leaders, the challenge isn't simply learning another AI tool.
                </p>
                <p>
                  It's deciding where AI belongs, where people should lead, and how to use AI in ways that strengthen your mission rather than distract from it.
                </p>
                <p className="text-brand-navy">
                  That's the work I help leadership teams do every day.
                </p>
              </div>
            </div>
          </div>
        </div>
      </Section>

      {/* Story & Credibility Section */}
      <Section bg="white" className="py-24">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col lg:flex-row gap-16">
            <div className="w-full lg:w-1/2">
              <h2 className="text-3xl font-bold text-brand-navy mb-8">My Story</h2>
              <div className="space-y-6 text-gray-700 text-lg leading-relaxed">
                <p className="font-bold text-brand-navy text-xl">
                  "I've spent my career helping organisations create impact. Leading AI well is simply the next chapter of that work."
                </p>
                <p>
                  For years I worked alongside charities, arts organisations and community organisations, helping them secure funding, deliver programmes and build stronger foundations.
                </p>
                <p>
                  As AI became part of everyday work, I noticed something worrying. Leaders weren't struggling because they lacked AI tools. They were struggling because no one was helping them decide how AI should work inside organisations built on trust, safeguarding and people.
                </p>
                <p>
                  AI CoLab Studio was created to close that gap. My value comes from combining social impact leadership with practical AI guidance, ensuring that technology never compromises human dignity or community trust.
                </p>
              </div>
            </div>
            <div className="w-full lg:w-1/2">
              <CredibilitySection />
            </div>
          </div>

          <PhilosophySection />
          <WorkProcess />
        </div>
      </Section>

      {/* Final CTA */}
      <Section bg="navy" className="py-24 text-center">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-3xl md:text-5xl font-bold text-white mb-6">Ready to lead AI with confidence?</h2>
          <p className="text-xl text-gray-300 mb-12">
            Whether you're exploring AI for the first time or looking to build confidence across your organisation, I'd love to hear about your goals.
          </p>
          <div className="flex flex-col sm:flex-row gap-6 justify-center">
            <Button href={claritySessionLink} variant="primary" className="bg-brand-gold text-brand-navy hover:bg-yellow-500 border-none px-10 py-5 text-lg font-bold">
              Book your AI Leadership Review
            </Button>
            <Button to="/contact" variant="outline" className="border-white text-white hover:bg-white hover:text-brand-navy px-10 py-5 text-lg font-bold">
              Get in Touch
            </Button>
          </div>
        </div>
      </Section>
    </MainLayout>
  );
};

export default About;